import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Loading } from "@/components/atoms";
import { Expired } from "@/components/pages";
import { useVerifyProviderTokenQuery, useLazyGetProviderQuery } from "@/redux/apis/auth/authApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";

/**
 * UserAccountInvite - Handles the pre-auth validation for user account invites (Admin, Manager, Dispatcher, Call Center)
 *
 * This is the first phase of the invite flow, before Kinde authentication.
 *
 * Flow:
 * 1. User clicks invite link from email → /dashboard/invite/{inviteKey}
 * 2. Verify the invite key is valid
 * 3. If a user is already logged in:
 *    - Save the invite path to sessionStorage and trigger Kinde logout
 *    - After logout, Login page detects the saved path and redirects back here
 * 4. If NOT authenticated:
 *    - Redirect to Kinde signup with utm_content: "user_account" and utm_term: inviteKey
 *
 * After Kinde auth, AuthCallback reads utm_content and utm_term from claims
 * and redirects to /onboarding/invite/{inviteKey} for the registration API call.
 */
export const UserAccountInvite = () => {
  const { inviteKey } = useParams<{ inviteKey: string }>();
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading: isAuthLoading, register, logout } = useKindeAuth();
  const hasProcessed = useRef(false);
  const activeOperator = useAppSelector(activeOperatorSelector);

  // Verify the invite token
  const {
    isLoading: isVerifying,
    isSuccess: isValidToken,
    data: tokenData,
    isError: isVerifyError,
  } = useVerifyProviderTokenQuery(inviteKey || "", {
    skip: !inviteKey,
  });

  const [getProvider] = useLazyGetProviderQuery();

  // Set active operator when token is verified
  useEffect(() => {
    if (isValidToken && tokenData?.provider_uuid) {
      getProvider(tokenData.provider_uuid)
        .unwrap()
        .then((operator) => {
          dispatch(setActiveOperator(operator));
        })
        .catch((error) => {
          console.error("Failed to fetch operator:", error);
        });
    }
  }, [isValidToken, tokenData, getProvider, dispatch]);

  // Main flow logic
  useEffect(() => {
    // Wait for verification and auth check to complete
    if (isVerifying || isAuthLoading) return;

    // Invalid token - don't proceed
    if (!isValidToken || !tokenData) return;

    // Prevent multiple executions
    if (hasProcessed.current) return;

    if (isAuthenticated) {
      hasProcessed.current = true;

      // Another user is already logged in — log them out first to ensure a fresh session.
      // Save the invite path so the Login page can redirect back here after logout completes.
      sessionStorage.setItem("pendingInviteRedirect", `/invite/${inviteKey}`);
      logout();
      return;
    }

    // Wait for active operator to be loaded (so we have orgCode for Kinde registration)
    if (!activeOperator) return;

    hasProcessed.current = true;

    // User is NOT authenticated — redirect to Kinde signup with UTM params.
    // utm_term carries the inviteKey through Kinde and back via claims.
    // loginHint pre-fills the email from the invite.
    register({
      orgCode: activeOperator.orgCode,
      loginHint: tokenData.email,
      properties: {
        utm_campaign: tokenData.provider_uuid,
        utm_source: "web-app",
        utm_content: "user_account",
        utm_term: inviteKey as string,
      },
    });
  }, [isVerifying, isAuthLoading, isAuthenticated, isValidToken, tokenData, inviteKey, register, logout, activeOperator]);

  // No invite key in URL - this is definitely an error
  if (!inviteKey) {
    return (
      <Expired
        title="Invitation Expired"
        description="Sorry, the invitation link is no longer valid. Please contact the person who invited you."
      />
    );
  }

  // Show error ONLY if the query has definitively failed (isError is true)
  if (isVerifyError) {
    return (
      <Expired
        title="Invitation Expired"
        description="Sorry, the invitation link is no longer valid. Please contact the person who invited you."
      />
    );
  }

  // For ALL other cases, show loading
  return <Loading />;
};
